import React, { useEffect, useRef, useState } from 'react';
import ErrorUtils from 'src/utils/ErrorUtils';
import { SavingStatus } from 'src/models/common/SavingStatus';
import TPEAction from 'src/models/common/TPEAction';
import AllocationGroup, { AllocationGroupFormula } from 'src/models/tp-allocation/AllocationGroup';
import ServiceCollection from 'src/services/ServiceCollection';
import { ACTIONS } from 'src/services/tp-allocation/allocation-groups/AllocationGroupsReducer';
import { AllocationGroupState } from 'src/services/tp-allocation/allocation-groups/AllocationGroupsState';
import {
    newAllocationGroupFormula, toAllocationGroupFormula, toCreateDASAllocationFormulaRequest, toUpdateDASAllocationFormulaRequest,
} from 'src/services/derived-allocation-sheet/dasAllocationGroupMapper';
import { validateDasFormula } from 'src/services/derived-allocation-sheet/dasAllocationFormulaChecks';

export const SAVE_RETURNED_NO_FORMULA_MESSAGE = 'The formula was saved, but the response did not include it. Refresh the page to see it.';

export interface DasAllocationFormulaWritesArgs {
    services: ServiceCollection;
    sheetId: string;
    groupsState: AllocationGroupState;
    groupsDispatch: React.Dispatch<TPEAction>;
    companies: string[];
    /** Called after every successful write, to refresh the sheet header. */
    onWritten: () => void;
}

/**
 * Saves, duplicates and deletes Step 3 formulas. An edit becomes a save when the reused TPA groups reducer sets
 * `updateFormulaPayload`; the formula is checked first, and a refusal shows as the group's error. Saves run one
 * at a time, in order: edits made while a save is running wait, only the latest edit of each formula is kept,
 * and an edit of a formula that was being created is saved as an update of the created one.
 * @returns Whether a formula write is running, and the duplicate, confirmed-remove and save-all actions.
 */
export default function useDasAllocationFormulaWrites({
    services, sheetId, groupsState, groupsDispatch, companies, onWritten,
}: DasAllocationFormulaWritesArgs) {
    const { groups, updateFormulaPayload, removeFormulaModalPayload } = groupsState;
    const [savingFormula, setSavingFormula] = useState(false);
    const inFlight = useRef(false);
    // The formula being saved now; Save does not send it again.
    const inFlightId = useRef<string | undefined>(undefined);
    const queued = useRef<AllocationGroupFormula[]>([]);
    const groupsRef = useRef(groups);
    groupsRef.current = groups;
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const setGroupError = (groupId: string, message?: string) =>
        groupsDispatch(ACTIONS.SET_GROUP_LEVEL_ERROR.withPayload({ groupId, message }));

    /** Starts the next queued save, if any. */
    const saveNextQueued = () => {
        const next = queued.current.shift();
        if (next != null) {
            saveFormula(next);
        }
    };

    const saveFormula = async (formula: AllocationGroupFormula) => {
        const group = groupsRef.current.find((g) => g.allocationGroupId === formula.allocationGroupId);
        const problem = validateDasFormula(formula, group?.formulas ?? [], companies);
        if (problem) {
            setGroupError(formula.allocationGroupId, problem);
            // A refused edit does not hold up the edits queued behind it.
            saveNextQueued();
            return;
        }
        inFlight.current = true;
        inFlightId.current = formula.allocationFormulaId;
        setSavingFormula(true);
        let savedId: string | undefined;
        const service = services.derivedAllocationSheetService;
        try {
            const { allocationFormula } = formula.isNew
                ? await service.createDASAllocationFormula(toCreateDASAllocationFormulaRequest(sheetId, formula))
                : await service.updateDASAllocationFormula(toUpdateDASAllocationFormulaRequest(sheetId, formula));
            if (!mountedRef.current) return;
            if (allocationFormula == null) {
                setGroupError(formula.allocationGroupId, SAVE_RETURNED_NO_FORMULA_MESSAGE);
                return;
            }
            savedId = allocationFormula.allocationFormulaId;
            groupsDispatch(ACTIONS.REFRESH_ALLOCATION_GROUP_FORMULA.withPayload({
                ...toAllocationGroupFormula(allocationFormula, formula.sequence), unsavedId: formula.allocationFormulaId, validationErrors: [],
            }));
            setGroupError(formula.allocationGroupId, undefined);
            onWritten();
        } catch (ex) {
            if (mountedRef.current) setGroupError(formula.allocationGroupId, ErrorUtils.getMessage(ex));
        } finally {
            inFlight.current = false;
            inFlightId.current = undefined;
            if (mountedRef.current) {
                setSavingFormula(false);
                if (savedId != null && formula.isNew) {
                    // Edits queued behind a create of the same formula save as updates of the created one.
                    queued.current = queued.current.map((q) => (q.allocationFormulaId === formula.allocationFormulaId
                        ? { ...q, allocationFormulaId: savedId!, isNew: false } : q));
                }
                saveNextQueued();
            }
        }
    };

    /** Queues a save behind the running one, keeping only the latest edit of each formula. */
    const enqueue = (payload: AllocationGroupFormula) => {
        const index = queued.current.findIndex((q) => q.allocationFormulaId === payload.allocationFormulaId);
        if (index >= 0) {
            queued.current[index] = payload;
        } else {
            queued.current.push(payload);
        }
    };

    useEffect(() => {
        if (updateFormulaPayload == null) {
            return;
        }
        if (inFlight.current) {
            enqueue(updateFormulaPayload);
            return;
        }
        saveFormula(updateFormulaPayload);
    }, [updateFormulaPayload]);

    /**
     * Saves every formula that is new or has edits not saved yet, one at a time, in group order. The formula being
     * saved now is skipped, since its later edits are already queued. A formula that fails its check or its save
     * keeps its group error and stays unsaved.
     */
    const saveUnsavedFormulas = () => {
        groupsRef.current.flatMap((g) => g.formulas ?? [])
            .filter((f) => (f.isNew || f.savingStatus === SavingStatus.Unsaved) && f.allocationFormulaId !== inFlightId.current)
            .forEach((f) => enqueue({ ...f }));
        if (!inFlight.current) {
            saveNextQueued();
        }
    };

    /**
     * Adds a copy of the formula at the end of its group and saves it. The copy gets a free name and no company
     * codes, since the original already maps them.
     */
    const duplicateFormula = (group: AllocationGroup, formula: AllocationGroupFormula) => {
        const taken = new Set((group.formulas || []).map((f) => (f.description || '').trim().toLowerCase()));
        let description = `DUPLICATE - ${formula.description}`;
        for (let n = 2; taken.has(description.toLowerCase()); n++) {
            description = `DUPLICATE(${n}) - ${formula.description}`;
        }
        const copy = {
            ...newAllocationGroupFormula(group), description, expression: formula.expression, expressionTokens: formula.expressionTokens,
        };
        groupsDispatch(ACTIONS.ADD_ALLOCATION_GROUP_FORMULA.withPayload({ group, newFormula: copy }));
        groupsDispatch(ACTIONS.SET_UPDATE_FORMULA_PAYLOAD.withPayload({ ...copy }));
    };

    /** Deletes the saved formula the remove modal asked about; the other formulas keep their order. */
    const confirmRemoveFormula = async () => {
        const formula: AllocationGroupFormula | undefined = removeFormulaModalPayload?.formula;
        if (formula == null) return;
        setSavingFormula(true);
        try {
            await services.derivedAllocationSheetService.deleteDASAllocationFormula(sheetId, formula.allocationGroupId, formula.allocationFormulaId);
            if (!mountedRef.current) return;
            const group = groupsRef.current.find((g) => g.allocationGroupId === formula.allocationGroupId);
            if (group != null) {
                group.formulas = group.formulas.filter((f) => f.allocationFormulaId !== formula.allocationFormulaId);
            }
            groupsDispatch(ACTIONS.SET_ALLOCATION_GROUPS.withPayload([...groupsRef.current]));
            setGroupError(formula.allocationGroupId, undefined);
            onWritten();
        } catch (ex) {
            if (mountedRef.current) setGroupError(formula.allocationGroupId, ErrorUtils.getMessage(ex));
        } finally {
            if (mountedRef.current) {
                setSavingFormula(false);
                groupsDispatch(ACTIONS.SET_REMOVE_FORMULA_PAYLOAD.withPayload(null));
            }
        }
    };

    return { savingFormula, duplicateFormula, confirmRemoveFormula, saveUnsavedFormulas };
}
